"use client";

/**
 * Browser-side PostHog. Thin on purpose: initialisation, a typed `track()`, and
 * the two identifiers the server needs to join its events to this session.
 *
 * Like the server half, nothing here is allowed to affect the page. Every entry
 * point is guarded so a blocked script, a missing key or a private-mode storage
 * error degrades to "no analytics" rather than a broken checkout.
 */

import posthog, { type CaptureResult } from "posthog-js";
import type { EventName, EventProperties } from "./events";

const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
/**
 * Ingestion goes straight to PostHog's EU host. A same-origin `/ingest` rewrite
 * would survive ad blockers (worth perhaps 10-30% more events), but it requires
 * `skipTrailingSlashRedirect` in `next.config.ts` — an app-wide routing change,
 * which this instrumentation deliberately avoids. See `docs/posthog.md`.
 */
const apiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://eu.i.posthog.com";
const uiHost = process.env.NEXT_PUBLIC_POSTHOG_UI_HOST || "https://eu.posthog.com";

let initialized = false;

/**
 * Super property set on staff browsers (see `markInternalBrowser`). Persisted
 * by the SDK, so every later event from that browser carries it — including
 * shop pages an admin opens — and dashboards can filter internal traffic out.
 */
const INTERNAL_PROPERTY = "is_internal";
/** Set by `markInternalBrowser` when it runs before `initPostHog`. */
let internalPending = false;

/**
 * Orders this browser started a Stripe checkout for. The order page is also
 * opened from the admin panel and from the confirmation email (possibly
 * forwarded), so the email on the order only belongs to this browser's person
 * when the checkout began here.
 */
const CHECKOUT_ORDERS_KEY = "ph_checkout_orders";
const MAX_CHECKOUT_ORDERS = 20;

/**
 * Mirrors the Firebase Analytics rule in `src/lib/firebase.ts`: real traffic
 * only. Set `NEXT_PUBLIC_POSTHOG_DEBUG=true` to capture from localhost while
 * working on instrumentation.
 */
function shouldCapture(): boolean {
  if (typeof window === "undefined") return false;
  if (!apiKey) return false;
  if (process.env.NEXT_PUBLIC_POSTHOG_DEBUG === "true") return true;
  const host = window.location.hostname;
  return host !== "localhost" && host !== "127.0.0.1";
}

const SIGN_TOKEN_RE = /\/sign\/[0-9a-f]{64}/g;

function redactSignTokens(event: CaptureResult | null): CaptureResult | null {
  if (!event?.properties) return event;
  for (const [key, value] of Object.entries(event.properties)) {
    if (typeof value === "string" && value.includes("/sign/")) {
      event.properties[key] = value.replace(SIGN_TOKEN_RE, "/sign/[token]");
    }
  }
  return event;
}

export function initPostHog(): void {
  if (initialized || !shouldCapture()) return;
  try {
    posthog.init(apiKey as string, {
      api_host: apiHost,
      // Where "open in PostHog" links point (the app, not the ingestion host).
      ui_host: uiHost,
      // The App Router does full client-side navigation, so let the SDK watch
      // history rather than wiring a pathname effect by hand.
      capture_pageview: "history_change",
      capture_pageleave: true,
      // Buyers are anonymous until Stripe gives us an email, so only create
      // person profiles once we actually identify someone.
      person_profiles: "identified_only",
      // Client-side error tracking — the "potential errors" half of the brief.
      capture_exceptions: true,
      // Autocapture stays off: this taxonomy is deliberate, and autocapture
      // would bury the payment events under thousands of DOM clicks.
      autocapture: false,
      capture_performance: true,
      persistence: "localStorage+cookie",
      // Contract signing links carry their only credential in the path, so
      // keep the token out of analytics (URL, pathname, referrer).
      before_send: redactSignTokens,
      loaded: (ph) => {
        if (process.env.NEXT_PUBLIC_POSTHOG_DEBUG === "true") ph.debug();
      },
    });
    initialized = true;
    // Before the first pageview, so the entry page is flagged too.
    if (internalPending) applyInternalMark();

    // `history_change` is documented to capture the initial pageview too, but it
    // does not when `init()` runs from an effect after hydration, which is the
    // case here — verified against this app, where landing pages produced a
    // `$pageleave` with no matching `$pageview`. Entry pages are exactly what a
    // funnel needs, so capture it explicitly, guarding against a double count
    // if the SDK did fire it after all.
    const alreadyCaptured = (
      posthog as unknown as { _initialPageviewCaptured?: boolean }
    )._initialPageviewCaptured;
    if (!alreadyCaptured) posthog.capture("$pageview");
  } catch {
    // Never let analytics initialisation break a page render.
  }
}

/** Capture a typed event. No-ops when PostHog isn't running. */
export function track<E extends EventName>(
  event: E,
  properties: EventProperties[E]
): void {
  if (!shouldCapture()) return;
  try {
    posthog.capture(event, { ...properties, source: "client" });
  } catch {
    // Swallowed: a failed capture must never surface to a customer.
  }
}

/**
 * Attaches the buyer's email to the person once Stripe has collected it, so
 * pre-purchase anonymous activity and the order are the same person in PostHog.
 *
 * Only when this browser started the checkout for `orderId`, and never on a
 * staff browser: an admin opening a customer's order page must not become that
 * customer, or everything the admin does afterwards is recorded as theirs.
 */
export function identifyBuyer(
  orderId: string,
  email: string | null | undefined,
  properties: Record<string, unknown> = {}
): void {
  if (!shouldCapture() || !email) return;
  try {
    if (isInternalBrowser() || !readCheckoutOrders().includes(orderId)) return;
    posthog.identify(email, { email, ...properties });
  } catch {
    // Ignored.
  }
}

/** Records that this browser is about to pay for `orderId`; see `identifyBuyer`. */
export function rememberCheckoutOrder(orderId: string): void {
  try {
    const orders = readCheckoutOrders().filter((id) => id !== orderId);
    orders.push(orderId);
    localStorage.setItem(
      CHECKOUT_ORDERS_KEY,
      JSON.stringify(orders.slice(-MAX_CHECKOUT_ORDERS))
    );
  } catch {
    // Private mode or blocked storage: the buyer just stays anonymous.
  }
}

function readCheckoutOrders(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(CHECKOUT_ORDERS_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
}

/**
 * Flags this browser as staff, from the admin layout. The first time, it also
 * drops whatever person the browser was identified as (before `identifyBuyer`
 * checked for the buyer, opening an order from the admin panel identified the
 * admin as that customer), so admin activity stops landing on customers.
 *
 * The flag deliberately survives logout: it describes the device, not the
 * session, and staff still browse the shop from it.
 */
export function markInternalBrowser(): void {
  if (!shouldCapture()) return;
  try {
    internalPending = true;
    // Admin effects run before the root layout's, so PostHog may not be up yet;
    // `initPostHog` applies the mark itself in that case.
    if (initialized) applyInternalMark();
    else initPostHog();
  } catch {
    // Ignored.
  }
}

function applyInternalMark(): void {
  if (isInternalBrowser()) return;
  posthog.reset();
  posthog.register({ [INTERNAL_PROPERTY]: true });
}

function isInternalBrowser(): boolean {
  return posthog.get_property(INTERNAL_PROPERTY) === true;
}

/**
 * The identifiers the browser hands to `/api/checkout`, which stores them in
 * Stripe metadata so webhook-fired events (notably `payment_failed`) can be
 * attributed to this person and this session. Returns nulls when PostHog isn't
 * running — the server falls back to an order-scoped id.
 */
export function getPostHogLinkIds(): {
  phDistinctId: string | null;
  phSessionId: string | null;
} {
  if (!shouldCapture()) return { phDistinctId: null, phSessionId: null };
  try {
    return {
      phDistinctId: posthog.get_distinct_id() ?? null,
      phSessionId: posthog.get_session_id() ?? null,
    };
  } catch {
    return { phDistinctId: null, phSessionId: null };
  }
}

/** Escape hatch for the rare case a caller needs the raw SDK. */
export { posthog };
